import { useMutation } from '@apollo/client'
import { Form, ModalForm, TextInput, useForm } from '@faceup/form'
import { Card, ContentLayout } from '@faceup/ui'
import { Button, Col, Row, Space, Tooltip } from '@faceup/ui-base'
import { yup } from '@faceup/validation'
import { useState } from 'react'
import { graphql } from '../__generated__'

const schema = yup.object({
  previewId: yup
    .string()
    .required(
      'Final URL should look like: https://newsletter.faceup.com/preview/789425/emails/123456'
    ),
})

const testEmailSchema = yup.object({
  subject: yup.string().required('Subject is required'),
  toEmailPrefix: yup.string().required('Email prefix is required'),
  fromName: yup.string().required('From name is required'),
  fromEmailPrefix: yup.string().required('From email prefix is required'),
})

const sendToPlanSchema = yup.object({
  subject: yup.string().required('Subject is required'),
  fromName: yup.string().required('From name is required'),
  fromEmailPrefix: yup.string().required('From email prefix is required'),
})

const mutations = {
  GetMailerLiteEmail: graphql(`
    mutation GetMailerLiteEmailMutation($input: GetMailerLiteEmailInput!) {
      getMailerLiteEmail(input: $input) {
        html
      }
    }
  `),
  SendMailerLiteTestEmail: graphql(`
    mutation SendMailerLiteTestEmailMutation($input: SendMailerLiteTestEmailInput!) {
      sendMailerLiteTestEmail(input: $input) {
        success
      }
    }
  `),
  SendMailerLiteEmails: graphql(`
    mutation SendMailerLiteEmailsMutation($input: SendMailerLiteEmailsInput!) {
      sendMailerLiteEmails(input: $input) {
        success
      }
    }
  `),
}

export const SendEmailsView = () => {
  const [iframeHtml, setIframeHtml] = useState<string | null>(null)
  const [showTestEmailModal, setShowTestEmailModal] = useState(false)
  const [showSendToEveryoneModal, setShowSendToEveryoneModal] = useState(false)
  const [testEmailSent, setTestEmailSent] = useState(false)

  const [getMailerLiteEmail, { loading }] = useMutation(mutations.GetMailerLiteEmail, {
    onCompleted: data => {
      if (data?.getMailerLiteEmail?.html) {
        setIframeHtml(data.getMailerLiteEmail.html)
      }
    },
  })

  const [sendTestEmail] = useMutation(mutations.SendMailerLiteTestEmail, {
    onCompleted: () => {
      setShowTestEmailModal(false)
      setTestEmailSent(true)
    },
  })

  const [sendToEveryone] = useMutation(mutations.SendMailerLiteEmails, {
    onCompleted: () => {
      setShowSendToEveryoneModal(false)
    },
  })

  const form = useForm({
    schema,
    afterSubmit: 'resetValues',
  })

  const testEmailForm = useForm({
    schema: testEmailSchema,
    afterSubmit: 'resetValues',
  })

  const sendToPlanForm = useForm({
    schema: sendToPlanSchema,
    afterSubmit: 'resetValues',
  })

  const handlePreview = async ({ previewId }: { previewId: string }) => {
    await getMailerLiteEmail({
      variables: { input: { previewId } },
    })
    return true
  }

  const handleClear = () => {
    setIframeHtml(null)
    setTestEmailSent(false)
  }

  const handleSendTestEmail = async ({
    toEmailPrefix,
    subject,
    fromName,
    fromEmailPrefix,
  }: {
    toEmailPrefix: string
    subject: string
    fromName: string
    fromEmailPrefix: string
  }) => {
    if (!iframeHtml) {
      return false
    }

    await sendTestEmail({
      variables: {
        input: {
          toEmailPrefix,
          subject,
          html: iframeHtml,
          fromName,
          fromEmailPrefix,
        },
      },
    })
    return true
  }

  const handleSendToEveryone = async ({
    subject,
    fromName,
    fromEmailPrefix,
  }: {
    subject: string
    fromName: string
    fromEmailPrefix: string
  }) => {
    if (!iframeHtml) {
      return false
    }

    await sendToEveryone({
      variables: {
        input: {
          subject,
          html: iframeHtml,
          fromName,
          fromEmailPrefix,
          shouldSynchronizeAllRegions: true,
        },
      },
    })

    handleClear()

    return true
  }

  return (
    <ContentLayout header={<ContentLayout.Header title='Send emails' />}>
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        <Card>
          <Row style={{ padding: '24px' }}>
            <Col span={24}>
              {!iframeHtml && (
                <Form
                  form={form}
                  onSubmit={handlePreview}
                  buttonsPosition='under'
                  customSubmitButtonText={loading ? 'Loading...' : 'Preview'}
                  customSubmitSuccessText='Preview loaded!'
                >
                  <TextInput
                    name='previewId'
                    label='Enter the MailerLite preview ID from created campaign. You can find it after opening a campaign from https://dashboard.mailerlite.com/campaigns/status/draft'
                    control={form.control}
                    addonBefore='https://newsletter.faceup.com/preview/789425/emails/'
                  />
                </Form>
              )}
              {iframeHtml && (
                <>
                  <Space direction='vertical' style={{ width: '100%' }}>
                    <iframe
                      title='Preview'
                      srcDoc={iframeHtml}
                      style={{
                        width: '100%',
                        minHeight: 500,
                        border: 'none',
                        overflow: 'hidden',
                        borderRadius: 8,
                      }}
                    />
                    <Space>
                      <Button onClick={handleClear}>Clear</Button>
                      <Button type='primary' onClick={() => setShowTestEmailModal(true)}>
                        Send Test Email
                      </Button>
                      <Tooltip
                        title={
                          testEmailSent
                            ? ''
                            : 'Sending test email is required before sending to everyone'
                        }
                      >
                        <Button
                          disabled={!testEmailSent}
                          onClick={() => setShowSendToEveryoneModal(true)}
                        >
                          Send to everyone
                        </Button>
                      </Tooltip>
                    </Space>
                  </Space>

                  {/* Test Email Modal */}
                  <ModalForm
                    open={showTestEmailModal}
                    onCancel={() => setShowTestEmailModal(false)}
                    title='Send Test Email'
                    form={testEmailForm}
                    onSubmit={handleSendTestEmail}
                    customSubmitSuccessText='Test email sent!'
                    customSubmitButtonText='Send Test Email'
                  >
                    <TextInput name='subject' label='Subject' control={testEmailForm.control} />
                    <TextInput
                      name='toEmailPrefix'
                      label='To Email'
                      control={testEmailForm.control}
                      addonAfter='@faceup.com'
                    />
                    <TextInput name='fromName' label='From Name' control={testEmailForm.control} />
                    <TextInput
                      name='fromEmailPrefix'
                      label='From Email'
                      control={testEmailForm.control}
                      addonAfter='@faceup.com'
                    />
                  </ModalForm>

                  {/* Send to everyone Modal */}
                  <ModalForm
                    open={showSendToEveryoneModal}
                    onCancel={() => setShowSendToEveryoneModal(false)}
                    title='Send to everyone'
                    form={sendToPlanForm}
                    onSubmit={handleSendToEveryone}
                    customSubmitSuccessText='Emails are gradually sending. You can close this page. Just sit and relax.'
                    customSubmitButtonText='Send to Everyone'
                  >
                    <TextInput name='subject' label='Subject' control={sendToPlanForm.control} />
                    <TextInput name='fromName' label='From Name' control={sendToPlanForm.control} />
                    <TextInput
                      name='fromEmailPrefix'
                      label='From Email'
                      control={sendToPlanForm.control}
                      addonAfter='@faceup.com'
                    />
                  </ModalForm>
                </>
              )}
            </Col>
          </Row>
        </Card>
      </Space>
    </ContentLayout>
  )
}
